import moment from 'moment';

/** API may return project.config as object or JSON string */
export const parseProjectConfig = (config) => {
  if (config == null) return {};
  if (typeof config === 'string') {
    const t = config.trim();
    if (!t) return {};
    try {
      const p = JSON.parse(t);
      return p && typeof p === 'object' && !Array.isArray(p) ? p : {};
    } catch {
      return {};
    }
  }
  if (typeof config === 'object' && !Array.isArray(config)) return { ...config };
  return {};
};

export const formatProjectConfigDate = (date) => {
  if (date == null || date === '') return undefined;
  const m = moment.isMoment(date) ? date : moment(date);
  return m.isValid() ? m.format('MM-DD-YYYY') : undefined;
};

export const parseProjectConfigDate = (value) => {
  if (value == null || value === '') return null;
  const m = moment(value, ['MM-DD-YYYY', 'YYYY-MM-DD'], true);
  if (m.isValid()) return m;
  const fallback = moment(value);
  return fallback.isValid() ? fallback : null;
};

/** Safe display for project list start/end dates — null or invalid values show a dash. */
export const formatProjectListDate = (value, { empty = '—' } = {}) => {
  if (value == null || value === '') return empty;
  if (moment.isMoment(value)) {
    return value.isValid() ? value.format('DD MMM YYYY') : empty;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return moment(value).format('DD MMM YYYY');
  }
  const trimmed = String(value).trim();
  if (!trimmed || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') {
    return empty;
  }
  const strict = moment(trimmed, ['MM-DD-YYYY', 'YYYY-MM-DD', 'DD-MMM-YYYY'], true);
  if (strict.isValid()) return strict.format('DD MMM YYYY');
  const fallback = moment(trimmed, moment.ISO_8601, true);
  if (fallback.isValid()) return fallback.format('DD MMM YYYY');
  return empty;
};

export const getSiteLocationLabel = (projectSiteType) => {
  if (projectSiteType === 'Onshore') return 'Facility / Location';
  if (projectSiteType === 'Offshore') return 'Vessel / Barge name';
  return 'Vessel / Barge name or Facility / Location';
};

export const getProjectSubcontractor = (project) => {
  const cfg = parseProjectConfig(project?.config);
  const fromConfig = cfg.subcontractor;
  if (fromConfig != null && String(fromConfig).trim() !== '') {
    return String(fromConfig).trim();
  }
  return '';
};

export const isOffshoreProject = (project) => {
  const cfg = parseProjectConfig(project?.config);
  const projectSiteType = cfg.projectSiteType ?? project?.projectSiteType ?? '';
  return String(projectSiteType || '').trim().toLowerCase() === 'offshore';
};

export const getProjectTypeLower = (project) =>
  String(project?.projectType ?? project?.project_type ?? '').trim().toLowerCase();

export const isCoatingProjectType = (project) =>
  getProjectTypeLower(project) === 'coating';

/** Offshore welding/both redesigned pipeline form (ID #, expected joints, internal liner, etc.). */
export const usesRedesignedPipelineForm = (project) =>
  isOffshoreProject(project) && !isCoatingProjectType(project);

/** DPR Quality tab — hidden for offshore welding/both (defect metrics live on Productivity). */
export const showDprQualityTab = (project) => !usesRedesignedPipelineForm(project);

/** Legacy onshore stalk / pipe string fields — hidden for offshore projects. */
export const showOnshoreLegacyPipelineIds = (project) => !isOffshoreProject(project);

/** Welding measurements (OD, WT, expected length) — not shown for coating-only projects. */
export const showWeldingPipelineMeasurements = (project) =>
  !isCoatingProjectType(project);

/** Normalized display shape for read-only views and edit form hydration */
export const getProjectDisplayFields = (project) => {
  const cfg = parseProjectConfig(project?.config);
  const projectSiteType = cfg.projectSiteType || '';
  return {
    company: project?.finalClient || '',
    clientContractor: project?.clientName || '',
    subcontractor: cfg.subcontractor || '',
    country: project?.location || '',
    projectSiteType,
    siteLocationDetail: cfg.siteLocationDetail || '',
    siteLocationLabel: getSiteLocationLabel(projectSiteType),
    crceManager: project?.manager || '',
    crceRepresentative: cfg.crceRepresentative || '',
    crceRef: project?.pihRefId || '',
    contractId: project?.contractId || '',
    contractor: project?.contractor || project?.clientName || '',
  };
};

/**
 * Resolve the project `pihRefId` (CRCE Ref #) for the API payload.
 * Backend requires a non-null value, so fall back to Contract # then Project #
 * when the optional CRCE Ref field is left blank.
 */
export const resolveProjectPihRefId = (data) => {
  const candidates = [data?.pihRefId, data?.crceRef, data?.contractId, data?.projectId];
  for (let i = 0; i < candidates.length; i += 1) {
    const value = candidates[i];
    if (value != null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return undefined;
};

export const buildProjectConfigExtras = (data) => {
  const extras = {};
  const subcontractor =
    data.subcontractor != null && String(data.subcontractor).trim() !== ''
      ? String(data.subcontractor).trim()
      : undefined;
  if (subcontractor) extras.subcontractor = subcontractor;
  if (data.projectSiteType) extras.projectSiteType = data.projectSiteType;
  if (data.siteLocationDetail != null && String(data.siteLocationDetail).trim() !== '') {
    extras.siteLocationDetail = String(data.siteLocationDetail).trim();
  }
  if (data.crceRepresentative != null && String(data.crceRepresentative).trim() !== '') {
    extras.crceRepresentative = String(data.crceRepresentative).trim();
  }
  return extras;
};

/** Merge config extras on update; clears optional fields when emptied */
export const mergeProjectConfigExtras = (prevConfig, data) => {
  const next = { ...parseProjectConfig(prevConfig) };
  const sub =
    data.subcontractor != null ? String(data.subcontractor).trim() : undefined;
  if (sub) next.subcontractor = sub;
  else delete next.subcontractor;

  if (data.projectSiteType) next.projectSiteType = data.projectSiteType;
  else delete next.projectSiteType;

  const site =
    data.siteLocationDetail != null ? String(data.siteLocationDetail).trim() : '';
  if (site) next.siteLocationDetail = site;
  else delete next.siteLocationDetail;

  const rep =
    data.crceRepresentative != null ? String(data.crceRepresentative).trim() : '';
  if (rep) next.crceRepresentative = rep;
  else delete next.crceRepresentative;

  // Actual Start/End were deferred / removed from the create-project form — drop if present.
  delete next.actualStartDate;
  delete next.actualEndDate;

  return next;
};
